// START IMPORTS
// Payments imports
import {
  PaymentsSelectionModal,
  RadioPaymentsModal,
  RecyclingModal,
  RecyclingModalV2,
} from '@ltvco/refresh-lib/payments';
// Theme and UI imports
import {
  Stack,
  Theme,
  Button,
  Text,
  IconButton,
} from '@ltvco/refresh-lib/theme';
// Utility imports
import { useScreenSize } from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import { useSession, useSnackbar, SnackbarKey } from '@ltvco/refresh-lib/v1';
// Vendor imports
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
// END IMPORTS

export const Recycling = ({
  isAuthenticated,
}: {
  isAuthenticated: boolean;
}) => {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { isMobile } = useScreenSize();

  const isRecyclingV2Test = useFeatureIsOn('pbr-1207');
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [snackBarId, setSnackBarId] = useState<SnackbarKey>();
  const [openRecyclingModal, setOpenRecyclingModal] = useState<boolean>(false);
  const [openPaymentsSelectionModal, setOpenPaymentsSelectionModal] =
    useState<boolean>(false);
  const [openPaymentsUpdateModal, setOpenPaymentsUpdateModal] =
    useState<boolean>(false);
  // END STATE_MANAGEMENT

  // START EVENT_HANDLERS
  const toggleRecyclingModal = () =>
    setOpenRecyclingModal((prevState) => !prevState);

  const togglePaymentsSelectionModal = () =>
    setOpenPaymentsSelectionModal((prevState) => !prevState);

  const togglePaymentsUpdateModal = () => {
    setOpenPaymentsUpdateModal(!openPaymentsUpdateModal);
    if (!openPaymentsSelectionModal) {
      togglePaymentsSelectionModal();
    }
  };

  function handleAddPaymentMethod() {
    setOpenPaymentsSelectionModal(false);
    setOpenPaymentsUpdateModal(true);
  }
  // END EVENT_HANDLERS

  // START EFFECTS
  useEffect(() => {
    if (!isAuthenticated) return;

    const openSnackBar = () => {
      const snackbarID = enqueueSnackbar(
        <Stack direction="row" alignItems="center">
          <Text>
            <b>Payment Method Update Required</b> <br /> Please update your
            payment method on file to continue accessing
          </Text>
          {isMobile && (
            <IconButton
              id="close-snackbar-mobile"
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => {
                snackbarID && closeSnackbar(snackbarID);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>,
        {
          variant: 'error',
          hideIconVariant: true,
          persist: true,
          action: (
            <Stack gap={1} flexDirection={'row'}>
              <Button
                onClick={togglePaymentsSelectionModal}
                variant="outlined"
                size="small"
                fullWidth
                sx={(theme: Theme) => ({
                  fontsize: theme.spacing(0.75),
                  maxHeight: '40px',
                  color: `${theme.palette.error.dark} !important`,
                  borderColor: 'white',
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: 'white',
                    color: `${theme.palette.error.dark}`,
                    borderColor: 'black',
                  },
                })}
              >
                Update Billing Info
              </Button>
              {!isMobile && (
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => {
                    snackbarID && closeSnackbar(snackbarID);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ),
        }
      );
      return snackbarID;
    };

    const hasUserInRecycling = account?.account.user_info?.user_in_recycling;
    if (hasUserInRecycling === undefined) return;
    const urlParams = new URLSearchParams(window.location.search);
    if (
      hasUserInRecycling &&
      (urlParams.get('bvppcanc') ||
        urlParams.get('bvpp') ||
        urlParams.get('token'))
    ) {
      const snackBarId = openSnackBar();
      setSnackBarId(snackBarId);
      return;
    }

    if (hasUserInRecycling && !snackBarId) {
      setOpenRecyclingModal(true);
      const snackBarId = openSnackBar();
      setSnackBarId(snackBarId);
      return;
    }
    if (!hasUserInRecycling && snackBarId) {
      setOpenRecyclingModal(false);
      closeSnackbar(snackBarId);
    }
  }, [account, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const handleOpenModal = () => {
      toggleRecyclingModal();
    };

    const handleOpenSnackBar = (event: CustomEvent) => {
      enqueueSnackbar(event.detail.message, {
        variant: event.detail.variant,
      });
    };

    window.addEventListener('openRecyclingModal', handleOpenModal);
    window.addEventListener(
      'openRecyclingFeedbackSnackbar',
      handleOpenSnackBar as EventListener
    );

    return () => {
      window.removeEventListener('openRecyclingModal', handleOpenModal);
      window.removeEventListener(
        'openRecyclingFeedbackSnackbar',
        handleOpenSnackBar as EventListener
      );
    };
  }, [isAuthenticated]);
  // END EFFECTS

  // START RENDER
  return (
    <>
      {isAuthenticated ? (
        <>
          {isRecyclingV2Test ? (
            <RecyclingModalV2
              open={openRecyclingModal}
              onClose={toggleRecyclingModal}
              openUpdatePayment={togglePaymentsSelectionModal}
            />
          ) : (
            <RecyclingModal
              open={openRecyclingModal}
              onClose={toggleRecyclingModal}
              openUpdatePayment={togglePaymentsSelectionModal}
            />
          )}

          <PaymentsSelectionModal
            open={openPaymentsSelectionModal}
            onClose={togglePaymentsSelectionModal}
            onAddPaymentMethod={handleAddPaymentMethod}
            cta="Use This Payment Method"
            isRecycling={true}
          />
          <RadioPaymentsModal
            isOpen={openPaymentsUpdateModal}
            onCloseHandle={togglePaymentsUpdateModal}
          />
        </>
      ) : (
        <></>
      )}
    </>
  );
  // END RENDER
};
